import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import {
  useCompaniesQuery,
  useCreateDocExtractionEvalSetMutation,
  usePreviewDocExtractionEvalSetMutation,
  type EvalSetFieldsFragment,
} from "../../generated/graphql";
import {
  APPROVAL_TIERS,
  DEFAULT_TARGET_SIZE,
  SELECTION_AXES,
  type ApprovalTier,
  type EvalSelectionConfig,
  type SelectionAxis,
  type StratumSummary,
} from "./eval-config";
import StrataTable from "./strata-table";

type CompanyOption = { value: string; label: string };

type PreviewResult = {
  totalCandidates: number;
  selectedCount: number;
  strata: StratumSummary[];
  poolCutoffAt: string | null;
};

/**
 * Builds a selection config, previews the resulting strata census, and freezes a
 * reusable eval set. Also hosts the existing-set picker (the reuse mechanism):
 * selecting a set here lifts it to the parent so the run launcher can target it.
 */
const EvalSetBuilder = ({
  sets,
  selectedSetId,
  onSelectSet,
  onSetCreated,
}: {
  readonly sets: EvalSetFieldsFragment[];
  readonly selectedSetId: string | null;
  readonly onSelectSet: (id: string | null) => void;
  readonly onSetCreated: (id: string) => void;
}) => {
  const [name, setName] = useState("");
  const [targetSize, setTargetSize] = useState(DEFAULT_TARGET_SIZE);
  const [seed, setSeed] = useState("1");
  const [axes, setAxes] = useState<SelectionAxis[]>(["company"]);
  const [approvalTiers, setApprovalTiers] = useState<ApprovalTier[]>([
    "human_approved",
  ]);
  const [noveltyAxis, setNoveltyAxis] = useState(false);
  const [timeDisjoint, setTimeDisjoint] = useState(false);
  const [requireOcrCache, setRequireOcrCache] = useState(true);
  const [minPerCompany, setMinPerCompany] = useState("");
  const [capPerCompany, setCapPerCompany] = useState("");
  const [companyAllowlist, setCompanyAllowlist] = useState<string[]>([]);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  // The config the currently-shown preview was computed for. Freezing is gated
  // on this matching the live config so the operator can only create a set whose
  // exact census they just saw — editing any field after a preview invalidates
  // it until they preview again.
  const [previewedConfigKey, setPreviewedConfigKey] = useState<string | null>(
    null,
  );

  const [runPreview, { loading: previewing, error: previewError }] =
    usePreviewDocExtractionEvalSetMutation();
  const [runCreate, { loading: creating, error: createError }] =
    useCreateDocExtractionEvalSetMutation();

  const { data: companiesData } = useCompaniesQuery({
    fetchPolicy: "cache-first",
  });

  const companyOptions = useMemo(
    (): CompanyOption[] =>
      (companiesData?.companies ?? []).map((company) => ({
        value: company.uuid,
        label: company.name,
      })),
    [companiesData?.companies],
  );

  const selectedCompanyOptions = useMemo(
    () =>
      companyOptions.filter((option) =>
        companyAllowlist.includes(option.value),
      ),
    [companyOptions, companyAllowlist],
  );

  const stratifiesByCompany = axes.includes("company");

  const config = useMemo((): EvalSelectionConfig => {
    const min = minPerCompany.trim() === "" ? undefined : Number(minPerCompany);
    const cap = capPerCompany.trim() === "" ? undefined : Number(capPerCompany);
    return {
      method: "proportional_stratified",
      targetSize,
      seed: seed.trim(),
      axes,
      noveltyAxis,
      approvalTiers,
      timeDisjoint,
      requireOcrCache,
      ...(min !== undefined && Number.isFinite(min)
        ? { minPerCompany: min }
        : {}),
      ...(cap !== undefined && Number.isFinite(cap)
        ? { capPerCompany: cap }
        : {}),
      ...(companyAllowlist.length > 0 ? { companyAllowlist } : {}),
    };
  }, [
    targetSize,
    seed,
    axes,
    noveltyAxis,
    approvalTiers,
    timeDisjoint,
    requireOcrCache,
    minPerCompany,
    capPerCompany,
    companyAllowlist,
  ]);

  // Stable identity for the selection config, used to detect whether the shown
  // preview still reflects the live config (the keys above are all JSON-safe).
  const configKey = useMemo(() => JSON.stringify(config), [config]);

  const toggleAxis = (axis: SelectionAxis) => {
    setAxes((prev) =>
      prev.includes(axis) ? prev.filter((a) => a !== axis) : [...prev, axis],
    );
  };

  const toggleTier = (tier: ApprovalTier) => {
    setApprovalTiers((prev) =>
      prev.includes(tier) ? prev.filter((t) => t !== tier) : [...prev, tier],
    );
  };

  const handlePreview = async () => {
    const res = await runPreview({ variables: { config } });
    const data = res.data?.previewDocExtractionEvalSet;
    if (data !== undefined && data !== null) {
      setPreview({
        totalCandidates: data.totalCandidates,
        selectedCount: data.selectedCount,
        strata: (data.strata ?? []) as StratumSummary[],
        poolCutoffAt: data.poolCutoffAt ?? null,
      });
      setPreviewedConfigKey(configKey);
    }
  };

  const handleCreate = async () => {
    const res = await runCreate({
      variables: { input: { name: name.trim(), config } },
    });
    const created = res.data?.createDocExtractionEvalSet;
    if (created !== undefined && created !== null) {
      setPreview(null);
      setPreviewedConfigKey(null);
      setName("");
      onSetCreated(created.evalSetId);
    }
  };

  // A set can only be frozen from a preview that reflects the current config, so
  // the operator never creates a set whose census differs from what they saw.
  const previewMatchesConfig =
    preview !== null && previewedConfigKey === configKey;
  const canCreate =
    name.trim().length > 0 &&
    approvalTiers.length > 0 &&
    seed.trim().length > 0 &&
    previewMatchesConfig &&
    !creating;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography gutterBottom variant="h6">
        Eval sets
      </Typography>

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id="existing-set-label">Reuse an existing set</InputLabel>
        <Select
          labelId="existing-set-label"
          label="Reuse an existing set"
          value={selectedSetId ?? ""}
          onChange={(e) => {
            onSelectSet(e.target.value === "" ? null : e.target.value);
          }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {sets.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name} ({s.documentCount} docs)
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          or build a new set
        </Typography>
      </Divider>

      <Grid container spacing={2}>
        <Grid item xs={6} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Target size"
            value={targetSize}
            onChange={(e) => {
              setTargetSize(Number(e.target.value));
            }}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Seed"
            value={seed}
            onChange={(e) => {
              setSeed(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Min / company"
            value={minPerCompany}
            disabled={!stratifiesByCompany}
            onChange={(e) => {
              setMinPerCompany(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Cap / company"
            value={capPerCompany}
            disabled={!stratifiesByCompany}
            onChange={(e) => {
              setCapPerCompany(e.target.value);
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              <Typography variant="caption">Stratify axes</Typography>
            </FormLabel>
            <FormGroup>
              {SELECTION_AXES.map((axis) => (
                <FormControlLabel
                  key={axis}
                  control={
                    <Checkbox
                      size="small"
                      checked={axes.includes(axis)}
                      onChange={() => {
                        toggleAxis(axis);
                      }}
                    />
                  }
                  label={axis}
                />
              ))}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={noveltyAxis}
                    onChange={(e) => {
                      setNoveltyAxis(e.target.checked);
                    }}
                  />
                }
                label="novelty"
              />
            </FormGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              <Typography variant="caption">Approval tiers</Typography>
            </FormLabel>
            <FormGroup>
              {APPROVAL_TIERS.map((tier) => (
                <FormControlLabel
                  key={tier}
                  control={
                    <Checkbox
                      size="small"
                      checked={approvalTiers.includes(tier)}
                      onChange={() => {
                        toggleTier(tier);
                      }}
                    />
                  }
                  label={tier}
                />
              ))}
            </FormGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={timeDisjoint}
                  onChange={(e) => {
                    setTimeDisjoint(e.target.checked);
                  }}
                />
              }
              label="Time-disjoint (exclude pool overlap)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={requireOcrCache}
                  onChange={(e) => {
                    setRequireOcrCache(e.target.checked);
                  }}
                />
              }
              label="Require OCR cache"
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            multiple
            size="small"
            options={companyOptions}
            value={selectedCompanyOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Company allowlist (optional)"
                placeholder="Select companies"
              />
            )}
            onChange={(_, options) => {
              setCompanyAllowlist(options.map((option) => option.value));
            }}
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          disabled={previewing || approvalTiers.length === 0}
          onClick={handlePreview}
        >
          {previewing ? "Previewing…" : "Preview"}
        </Button>
        <TextField
          size="small"
          label="Set name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
        <Button
          variant="contained"
          disabled={!canCreate}
          onClick={handleCreate}
        >
          {creating ? "Creating…" : "Create / freeze"}
        </Button>
        {!previewMatchesConfig && (
          <Typography variant="caption" color="text.secondary">
            {preview === null
              ? "Preview before freezing a set."
              : "Config changed since preview — preview again before freezing."}
          </Typography>
        )}
      </Stack>

      {previewError !== undefined && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Preview failed: {previewError.message}
        </Alert>
      )}
      {createError !== undefined && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Create failed: {createError.message}
        </Alert>
      )}

      {preview !== null && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">
            {preview.selectedCount} selected of {preview.totalCandidates}{" "}
            candidates
            {preview.poolCutoffAt === null
              ? ""
              : ` · pool cutoff ${preview.poolCutoffAt}`}
          </Typography>
          <StrataTable strata={preview.strata} />
        </Box>
      )}
    </Paper>
  );
};

export default EvalSetBuilder;
